import { secureStorage } from './secureStorage'

const HARD_DISK_STORAGE_KEY = 'hardDiskId'
const DEVICE_ID_STORAGE_KEY = 'deviceId'

let memoryHardDiskId: string | null = null
let memoryDeviceId: string | null = null

/**
 * تشفير النص — مستخدم لمعرف الجهاز فقط (بدون استيراد apiClient).
 * نفس المدخلات تعطي دائماً نفس المخرجات (حتمي).
 */
export function encryptString(text: string): string {
  let hash1 = 0
  let hash2 = 0
  const salt = 'SmartAccount_HardDisk_2024'
  const combinedText = salt + text + salt

  for (let i = 0; i < combinedText.length; i++) {
    const char = combinedText.charCodeAt(i)
    hash1 = (hash1 << 5) - hash1 + char
    hash1 = hash1 & hash1
    hash2 = (hash2 << 7) - hash2 + char * 3
    hash2 = hash2 & hash2
  }

  const combined = `${hash1.toString(36)}${text}${hash2.toString(36)}`
  const base64 = btoa(unescape(encodeURIComponent(combined)))
  const reversed = base64.split('').reverse().join('')

  return `ENC_${reversed}_${Math.abs(hash1 + hash2).toString(36).toUpperCase()}`
}

function hashFingerprint(fingerprint: string): string {
  let hash = 0
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return `HD_${Math.abs(hash).toString(36).toUpperCase()}_${fingerprint.length}`
}

/**
 * محاولة قراءة الرقم التسلسلي الحقيقي للهارد ديسك (Electron / Node فقط).
 */
function tryReadPhysicalDiskSerial(): string | null {
  try {
    const win = window as unknown as {
      require?: (id: string) => unknown
      process?: { versions?: { electron?: string }; type?: string }
    }

    const isElectron =
      !!win.process?.versions?.electron ||
      win.process?.type === 'renderer' ||
      navigator.userAgent.toLowerCase().includes('electron')

    if (!isElectron || typeof win.require !== 'function') return null

    const { execSync } = win.require('child_process') as {
      execSync: (cmd: string, opts?: { encoding?: string; timeout?: number; windowsHide?: boolean }) => string
    }

    const commands = [
      'powershell -NoProfile -NonInteractive -Command "Get-CimInstance Win32_DiskDrive | Where-Object { $_.MediaType -match \'Fixed\' -or ($_.InterfaceType -match \'IDE|SCSI|NVMe|SATA\' -and $_.MediaType -notmatch \'External|Removable\') } | Sort-Object DeviceID | Select-Object -First 1 -ExpandProperty SerialNumber"',
      'powershell -NoProfile -NonInteractive -Command "(Get-CimInstance Win32_DiskDrive | Where-Object { $_.MediaType -match \'Fixed\' } | Sort-Object DeviceID | Select-Object -First 1).SerialNumber"',
      'wmic diskdrive where "MediaType like \'%%Fixed%%\'" get serialnumber',
    ]

    for (const cmd of commands) {
      try {
        const raw = execSync(cmd, { encoding: 'utf8', timeout: 8000, windowsHide: true })
        const serial = raw
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter((l) => l && !/^SerialNumber$/i.test(l))
          .join('')
          .replace(/\s+/g, '')

        if (serial && serial.length >= 4 && !/^0+$/.test(serial)) {
          return serial.toUpperCase()
        }
      } catch {
        // جرّب الأمر التالي
      }
    }
  } catch {
    // غير متاح في المتصفح
  }
  return null
}

/**
 * بصمة مستقرة قدر الإمكان — بدون screen size / language / timezone
 * (تتغيّر مع الدقة أو تغيير اللغة وتسبب License جديداً).
 */
function buildStableBrowserFingerprint(): string {
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string; architecture?: string; bitness?: string }
  }
  const parts = [
    nav.userAgentData?.platform || navigator.platform || '',
    nav.userAgentData?.architecture || '',
    nav.userAgentData?.bitness || '',
    String(navigator.hardwareConcurrency || 0),
    String(navigator.maxTouchPoints || 0),
  ]
  return parts.join('|')
}

function readStoredHardDiskId(): string | null {
  try {
    const existing = secureStorage.getItem(HARD_DISK_STORAGE_KEY)?.trim()
    if (existing && existing.startsWith('ENC_')) return existing
  } catch {
    /* ignore */
  }
  return null
}

function persistHardDiskId(id: string): void {
  try {
    secureStorage.setItem(HARD_DISK_STORAGE_KEY, id)
  } catch {
    /* ignore */
  }
}

export function getMachineName(): string {
  try {
    return (navigator.platform || 'Unknown').toString().slice(0, 100)
  } catch {
    return 'Unknown'
  }
}

/**
 * معرف الجهاز الثابت المبني على Serial Number للهارد ديسك (بعد التشفير).
 * يُحسب مرة واحدة ويُخزَّن في الذاكرة — لا execSync على كل طلب API.
 */
export function generateHardDiskId(): string {
  if (memoryHardDiskId) return memoryHardDiskId

  // Never execSync on the request hot path — reuse the sticky id.
  const stored = readStoredHardDiskId()
  if (stored) {
    memoryHardDiskId = stored
    return stored
  }

  const physicalSerial = tryReadPhysicalDiskSerial()
  if (physicalSerial) {
    const encryptedId = encryptString(`HDD_${physicalSerial}`)
    persistHardDiskId(encryptedId)
    memoryHardDiskId = encryptedId
    return encryptedId
  }

  const rawHardDiskId = hashFingerprint(buildStableBrowserFingerprint())
  const encryptedId = encryptString(rawHardDiskId)
  persistHardDiskId(encryptedId)
  memoryHardDiskId = encryptedId
  return encryptedId
}

/**
 * DeviceId ثابت للجهاز — يُحفظ مرة واحدة ولا يتغيّر مع Refresh.
 */
export function generateStableDeviceId(): string {
  if (memoryDeviceId) return memoryDeviceId

  try {
    const existing = secureStorage.getItem(DEVICE_ID_STORAGE_KEY)?.trim()
    if (existing) {
      memoryDeviceId = existing
      return existing
    }
  } catch {
    /* ignore */
  }

  let deviceId: string
  try {
    const uuid =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    deviceId = `SA-${uuid}`.toUpperCase()
  } catch {
    deviceId = `SA-${hashFingerprint(buildStableBrowserFingerprint())}`.toUpperCase()
  }

  try {
    secureStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId)
  } catch {
    /* ignore */
  }
  memoryDeviceId = deviceId
  return deviceId
}
